import { useState } from "react";
import { Home, FileText, LayoutTemplate } from "lucide-react";
import { useEditorStore } from "../store";
import { LayoutPicker } from "./LayoutPicker";

export function PagesTab() {
  const document = useEditorStore((s) => s.document);
  const activePageId = useEditorStore((s) => s.activePageId);
  const setActivePage = useEditorStore((s) => s.setActivePage);
  const addPage = useEditorStore((s) => s.addPage);
  const updatePage = useEditorStore((s) => s.updatePage);
  const deletePage = useEditorStore((s) => s.deletePage);
  const pageLayouts = useEditorStore((s) => s.pageLayouts);
  const [layoutPageId, setLayoutPageId] = useState<string | null>(null);
  const layoutPage = document.pages.find((p) => p.id === layoutPageId);

  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [editingPageId, setEditingPageId] = useState<string | null>(null);

  const handleAddPage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    addPage(newTitle.trim(), newSlug.trim() || undefined);
    setNewTitle("");
    setNewSlug("");
  };

  return (
    <div className="zirro-tab-content zirro-pages-tab">
      <div className="zirro-tab-header">
        <h3>Website Pages</h3>
        <span className="zirro-counter">{document.pages.length} / 20</span>
      </div>

      <ul className="zirro-page-list">
        {document.pages.map((page) => {
          const isHome = page.slug === "/";
          const isActive = page.id === activePageId;
          const isEditing = page.id === editingPageId;

          return (
            <li
              key={page.id}
              className={`zirro-page-item ${isActive ? "active" : ""}`}
            >
              <div
                className="zirro-page-item-header"
                onClick={() => setActivePage(page.id)}
              >
                <span className="zirro-page-icon">
                  {isHome ? <Home size={14} /> : <FileText size={14} />}
                </span>
                <div className="zirro-page-info">
                  <span className="zirro-page-title">{page.title}</span>
                  <span className="zirro-page-slug">{page.slug}</span>
                </div>
                {isHome ? (
                  <span className="zirro-badge zirro-badge-home">Home</span>
                ) : null}
                {pageLayouts.some((l) => l.pageSlug === page.slug) ? (
                  <button
                    type="button"
                    className="zirro-btn-text zirro-layout-trigger"
                    title="Choose a different layout for this page"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePage(page.id);
                      setLayoutPageId(page.id);
                    }}
                  >
                    <LayoutTemplate size={13} />
                    Layout
                  </button>
                ) : null}
                <button
                  type="button"
                  className="zirro-btn-text"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingPageId(isEditing ? null : page.id);
                  }}
                >
                  {isEditing ? "Done" : "Edit"}
                </button>
              </div>

              {isEditing ? (
                <div className="zirro-page-edit-form">
                  <label>
                    Page Title:
                    <input
                      type="text"
                      value={page.title}
                      onChange={(e) =>
                        updatePage(page.id, { title: e.target.value })
                      }
                    />
                  </label>
                  {!isHome ? (
                    <label>
                      URL Slug:
                      <input
                        type="text"
                        value={page.slug}
                        onChange={(e) =>
                          updatePage(page.id, { slug: e.target.value })
                        }
                      />
                    </label>
                  ) : null}
                  <label>
                    Navigation Label:
                    <input
                      type="text"
                      value={page.navLabel ?? ""}
                      placeholder={page.title}
                      onChange={(e) =>
                        updatePage(page.id, { navLabel: e.target.value })
                      }
                    />
                  </label>
                  <label className="zirro-checkbox-label">
                    <input
                      type="checkbox"
                      checked={page.showInNav !== false}
                      onChange={(e) =>
                        updatePage(page.id, { showInNav: e.target.checked })
                      }
                    />
                    Show in Navigation
                  </label>
                  {!isHome ? (
                    <button
                      type="button"
                      className="zirro-btn zirro-btn-danger"
                      onClick={() => deletePage(page.id)}
                    >
                      Delete Page
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {document.pages.length < 20 ? (
        <form onSubmit={handleAddPage} className="zirro-add-page-form">
          <h4>Add New Page</h4>
          <input
            type="text"
            placeholder="Page Title (e.g. Services)"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Slug (optional, e.g. /services)"
            value={newSlug}
            onChange={(e) => setNewSlug(e.target.value)}
          />
          <button type="submit" className="zirro-btn zirro-btn-secondary">
            + Create Page
          </button>
        </form>
      ) : (
        <p className="zirro-limit-warning">Maximum page limit (20) reached.</p>
      )}
      {layoutPage ? <LayoutPicker page={layoutPage} onClose={() => setLayoutPageId(null)} /> : null}
    </div>
  );
}
